import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import {BehaviorSubject, Observable, Subject, takeUntil} from 'rxjs';
import {Blade} from 'src/app/modules/blades/models/blade-component';
import {HistoryStore} from 'src/app/modules/core/store';
import {VehicleHistoryEventChange} from '../../../modules/core/store/history.store';
import {NotificationService} from '../../../modules/shared/services/notification.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {BlockActivityInfo, BlockActivityInfoUpdatedBy, VehicleHistory} from 'src/app/modules/shared/models/vehicle-history';

import * as _ from 'lodash';
import { ActivityCardComponent } from '../activityCard/activityCard.component';
import { MatDividerModule } from '@angular/material/divider';
import { ScrollSpyDirective } from '../../../modules/core/directives/scroll-spy.directive';
import { MatButtonModule } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';
import { BladeHeaderComponent } from '../../../modules/blades/components/blade-header/blade-header.component';
import { LoggingService } from '@modules/shared/services/logging.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-vehicle-history',
    templateUrl: './vehicleHistory.component.html',
    styleUrls: ['./vehicleHistory.component.scss'],
    imports: [
        BladeHeaderComponent, 
        TranslateModule, 
        MatButtonModule, 
        ScrollSpyDirective, 
        MatDividerModule, 
        ActivityCardComponent, 
        AsyncPipe
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
@Blade({
    name: 'VehicleHistoryComponent'
})
export class VehicleHistoryComponent implements OnInit, OnDestroy {
    private _historyStore = inject(HistoryStore);
    private _elem = inject(ElementRef);
    private _notificationService = inject(NotificationService);
    private _translate = inject(TranslateService);
    private _loggingService = inject(LoggingService);
    private _router = inject(Router);

    public bladeIndex: number;
    public title$: Observable<string|undefined>;

    public inventoryNumber: string;

    public blockActivities: BlockActivityInfo[] = [];
    public activeRoutePublicName = '-';
    public activeTripNumber = '-';

    public enabledSectionChange = true;

    public currentSectionId: string;

    private _title$: BehaviorSubject<string> = new BehaviorSubject(undefined);
    private _ngUnsubscribe$ = new Subject<void>();


    constructor() {
        this.title$ = this._title$.asObservable();
        // listens to history path line changes
        this._historyStore.getPropertyState('vehicleHistory')
            .pipe(
                takeUntil(this._ngUnsubscribe$)
            )
            .subscribe((vehicleHistory: VehicleHistory) => {
                if (vehicleHistory) {
                    this._title$.next(vehicleHistory.inventoryNumber);
                    this.inventoryNumber = vehicleHistory.inventoryNumber;
                    this.blockActivities = vehicleHistory.blockActivityInfo;
                    if (this.blockActivities != undefined && this.blockActivities.length !== 0) {
                        this._historyStore.selectActivity(this.blockActivities[0]);
                        this._historyStore.selectFirstPositionOfActivity(this.blockActivities[0]);
                    }
                    this._setEventSelectorIds();
                } else {
                    this._title$.next(undefined);
                    this.inventoryNumber = undefined;
                    this.blockActivities = [];
                    this.activeRoutePublicName = undefined;
                    this.activeTripNumber = undefined;
                }
            });

        this._historyStore.getPropertyState('selectedActivity')
            .pipe(
                takeUntil(this._ngUnsubscribe$)
            )
            .subscribe((activity: BlockActivityInfo) => {
                this.activeRoutePublicName = activity?.routePublicName ?? '-';
                this.activeTripNumber = activity?.tripNumber ?? '-';

                if (!activity || activity.selectorId === this.currentSectionId) {
                    this.currentSectionId = '';
                    this._inactivateAllVehicleHistoryEvents();
                    return;
                }

                this.currentSectionId = activity?.selectorId;

                if (activity.updatedBy == undefined) {
                    // do nothing
                } else if (activity.updatedBy === BlockActivityInfoUpdatedBy.scrubbarTimelineClick) {
                    this.enabledSectionChange = false;
                    // Scrolling to event does not happen here, see listener to map and scrubbar changes
                    return;
                } else if (activity.updatedBy === BlockActivityInfoUpdatedBy.mapCoordinateClick) {
                    this.enabledSectionChange = false;
                    this._scrollToSelectorId(activity.selectorId);
                    return;
                } else if (activity.updatedBy === BlockActivityInfoUpdatedBy.scroll) {
                    this.enabledSectionChange = false;
                    this._scrollToSelectorId(activity.selectorId);
                    this._historyStore.selectFirstPositionOfActivity(activity);
                    return;
                } else if (activity.updatedBy === BlockActivityInfoUpdatedBy.cardClick) {
                    return;
                }

                this.enabledSectionChange = false;
                this._scrollToSelectorId(activity?.selectorId);
                this._historyStore.selectFirstPositionOfActivity(activity);
            });

        this._historyStore.getPropertyState('mapOrScrubbarChange')
            .pipe(
                takeUntil(this._ngUnsubscribe$)
            )
            .subscribe((mapOrScrubbarChangeEvent: VehicleHistoryEventChange) => {
                this.enabledSectionChange = false;
                this._scrollToSelectorId(mapOrScrubbarChangeEvent?.eventSelectorId);
            });
    }

    ngOnInit(): void {
        this._loggingService.logPageView('History', this._router.url);
    }

    ngOnDestroy(): void {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
        this._historyStore.selectActivity(undefined);
        this._historyStore.updateHistoryData(undefined);
    }

    public activityClicked(activity: BlockActivityInfo) {
        this._historyStore.selectActivity(activity);
        this._historyStore.selectFirstPositionOfActivity(activity, false);
        this.currentSectionId = activity.selectorId;
        this._scrollToSelectorId(activity.selectorId);
    }

    public headerClicked() {
        const currentActivity = _.find(this.blockActivities, item => item.selectorId === this.currentSectionId);
        this._historyStore.selectFirstPositionOfActivity(currentActivity, false);
        if (this.currentSectionId !== '') {
            this._scrollToSelectorId(this.currentSectionId);
        } else {
            this._notificationService.openText(this._translate.instant('There is currently no active trip to scroll to'));
        }
    }

    /**
     * Focus on the currently active VehicleHistoryEvent by scrolling to it
     */
    public focusOnActiveEvent() {
        const vehicleHistoryEvents = this._historyStore.stateSnapshot?.selectedActivity?.events;
        if (!vehicleHistoryEvents) {
            this._notificationService.openText(this._translate.instant('There is currently no active event to scroll to'));
            return;
        }

        const activeEvent = _.find(vehicleHistoryEvents, e => e.isActive);
        if (activeEvent) {
            this._scrollToSelectorId(activeEvent.selectorId);
        }
    }

    public onSectionChange(sectionId: string) {
        if (sectionId === this.currentSectionId) {
            return;
        }

        // Stop auto-detected updates of activity if there is another active update with scrolling
        if (!this.enabledSectionChange) {
            return;
        }

        const currentActivity = _.find(this.blockActivities, item => item.selectorId === sectionId);
        currentActivity.updatedBy = BlockActivityInfoUpdatedBy.scroll;
        this._historyStore.selectActivity(currentActivity);
    }

    /**
     * Track when scroll stops
     */
    public onScrollEnd() {
        // When scroll has stopped, enable sectionChange again
        this.enabledSectionChange = true;
    }

    /**
     * Prepare all the selector ids of the Vehicle History Events, the ids are used to scroll to the event in the UI
     *
     * @private
     */
    private _setEventSelectorIds(): void {
        for (const blockActivity of this.blockActivities) {
            for (let i = 0; i < blockActivity.events.length; i++){
                const vehicleHistoryEvent = blockActivity.events[i];
                vehicleHistoryEvent.selectorId = `t${blockActivity.tripNumber}r${blockActivity.routePublicName}e${i}`;
            }
        }
    }

    /**
     * Scrolls to an item in the html, where the [attr.id] matches the selectorId input.
     *
     * @param selectorId - The id that matches the [attr.id] tag.
     * @param centralize
     * @private
     */
    private _scrollToSelectorId(selectorId: string, centralize = false): void  {
        if (selectorId == undefined || selectorId.trim() === '') {
            return;
        }

        const targetElement = this._elem.nativeElement.querySelector('#' + CSS.escape(selectorId));
        if (targetElement) {
            targetElement.scrollIntoView({behavior: 'smooth', block: centralize ? 'center' : 'start'});
        }
    }

    /**
     * Sets 'isActive' flag to false for all VehicleHistoryEvents, of all BlockActivities
     *
     * @private
     */
    private _inactivateAllVehicleHistoryEvents(): void{
        this.blockActivities.forEach(activity => {
            activity.events.forEach(e =>{
                e.isActive = false;
            });
        });
    }
}
